import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import CategorySection from './components/CategorySection';
import HotTopicsSection from './components/HotTopicsSection';
import FeaturedWorksSection from './components/FeaturedWorksSection';
import StatsSection from './components/StatsSection';
import TestimonialsSection from './components/TestimonialsSection';
import FAQSection from './components/FAQSection';

export default function HomePage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('website_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('感谢你的关注！');
      return;
    }
    const formData = new FormData(form);
    try {
      const res = await fetch('https://readdy.ai/api/form/d9q9rime3oq4jqumtiq0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed;
      try { parsed = JSON.parse(text); } catch { parsed = null; }
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢你的关注！我们将第一时间通知你cy社区的最新动态。');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || text || '提交失败，请稍后再试';
        if (msg.toLowerCase().includes('spam')) {
          setFormStatus('error');
          setFormMessage('提交失败，请稍后再试');
        } else {
          setFormStatus('error');
          setFormMessage(msg);
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查连接后重试');
    }
  };

  const heroBgUrl = 'https://readdy.ai/api/search-image?query=Vibrant%20creative%20youth%20community%20space%20with%20colorful%20abstract%20geometric%20shapes%2C%20warm%20orange%20and%20teal%20gradient%20background%2C%20modern%20artistic%20illustration%20style%2C%20diverse%20young%20people%20silhouettes%20creating%20art%20together%2C%20energetic%20atmosphere%2C%20inspirational%20creative%20hub%20concept%2C%20soft%20light%20particles%20floating%2C%20dreamy%20ethereal%20aesthetic&width=1800&height=1000&seq=hero-cy-community&orientation=landscape';

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full min-h-[600px] md:min-h-[700px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={heroBgUrl}
              alt="cy社区 - 创意青年聚集地"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/55"></div>

          <div className="relative z-10 w-full px-4 md:px-6 text-center max-w-4xl mx-auto py-20">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-background-50 leading-tight mb-6">
              创意青年聚集地
              <br />
              <span className="text-primary-300">发现无限可能</span>
            </h1>
            <p className="text-base md:text-lg text-background-50/80 max-w-2xl mx-auto mb-10 leading-relaxed">
              cy社区汇聚数万创意青年，在这里结交志同道合的伙伴、分享创意作品、参与热门话题讨论。释放你的创造力，探索属于你的精彩圈子。
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/category/all"
                className="px-8 py-3.5 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-all duration-200 whitespace-nowrap no-underline shadow-lg shadow-primary-500/25"
              >
                探索精彩内容
              </Link>
              <Link
                to="/about"
                className="px-8 py-3.5 rounded-full border border-background-50/40 text-background-50 font-medium text-sm hover:bg-background-50/10 transition-all duration-200 whitespace-nowrap no-underline"
              >
                了解更多
              </Link>
            </div>

            <div className="mt-12 flex items-center justify-center gap-6 text-background-50/60 text-xs">
              <span className="flex items-center gap-1.5">
                <i className="ri-shield-check-line"></i> 免费注册
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-group-line"></i> 12,800+ 创作者
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-gallery-line"></i> 58,000+ 作品
              </span>
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <CategorySection />

        {/* Hot Topics */}
        <HotTopicsSection />

        {/* Featured Works */}
        <FeaturedWorksSection />

        {/* Stats */}
        <StatsSection />

        {/* Testimonials */}
        <TestimonialsSection />

        {/* FAQ */}
        <FAQSection />

        {/* CTA Section */}
        <section className="w-full px-4 md:px-6 py-16 bg-accent-500">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-4">
              准备好释放你的创造力了吗？
            </h2>
            <p className="text-sm text-background-50/80 mb-8">
              加入cy社区，与数万创意达人共同成长，发现属于你的精彩圈子。
            </p>

            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="flex flex-col sm:flex-row items-center justify-center gap-3"
            >
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="form-hp"
              />
              <input
                type="email"
                name="email"
                required
                placeholder="输入你的邮箱地址"
                className="w-full sm:w-auto flex-1 max-w-sm px-5 py-3 rounded-full bg-background-50 text-foreground-900 text-sm placeholder:text-foreground-400 border-2 border-transparent focus:border-primary-300 focus:outline-none transition-colors"
              />
              <button
                type="submit"
                className="w-full sm:w-auto px-8 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                免费注册
              </button>
            </form>

            {formStatus !== 'idle' && (
              <p
                className={`mt-4 text-sm ${
                  formStatus === 'success' ? 'text-background-50' : 'text-red-200'
                }`}
              >
                {formMessage}
              </p>
            )}
          </div>
        </section>
      </main>

      <Footer />

      {/* Schema.org JSON-LD for Homepage */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'cy社区 - 创意青年聚集地，发现无限可能',
            description:
              'cy社区是汇聚创意青年的互动平台，结交志同道合的伙伴、分享创意作品、参与热门话题讨论。加入cy社区，释放你的创造力。',
            url: 'https://cycommunity.com',
            isPartOf: {
              '@type': 'WebSite',
              name: 'cy社区',
              url: 'https://cycommunity.com',
            },
            about: {
              '@type': 'Organization',
              name: 'cy社区',
              url: 'https://cycommunity.com',
              description:
                'cy社区是汇聚创意青年的互动平台，结交志同道合的伙伴、分享创意作品、参与热门话题讨论。',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: 'cy社区是免费的吗？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '是的，cy社区对所有创意青年完全免费开放！你可以免费注册、浏览内容、发布作品、参与讨论。',
                },
              },
              {
                '@type': 'Question',
                name: '如何在cy社区发布我的作品？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '注册账号后，点击页面顶部的发布作品按钮，选择作品分类，上传你的作品，填写描述和标签即可发布。',
                },
              },
              {
                '@type': 'Question',
                name: 'cy社区有哪些创意分类？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'cy社区涵盖视觉设计、摄影摄像、文字创作、音乐音频、科技创意、手工制作、生活方式等七大创意领域。',
                },
              },
              {
                '@type': 'Question',
                name: '我可以和其他创作者合作吗？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '当然可以！cy社区鼓励创作者之间的合作与交流。你可以在作品下方留言互动，通过私信联系感兴趣的创作者。',
                },
              },
              {
                '@type': 'Question',
                name: '如何保护我的原创作品版权？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'cy社区提供作品首发时间戳认证功能，所有发布的作品都会自动记录发布时间。发现侵权行为可通过举报功能维权。',
                },
              },
            ],
          }),
        }}
      />
    </>
  );
}